import { type JSX, type ReactNode } from 'react';

import { pfadVon, type Maske } from '../wegweiser';

/**
 * EIN ECHTER VERWEIS AUF EINE MASKE (Auftrag CRM-67, verallgemeinert mit CRM-69).
 *
 * Ein Verweis mit Adresse und kein Knopf: Strg-Klick und Mittelklick öffnen
 * die Maske im neuen Tab, und das tut der Browser selbst. Den gewöhnlichen
 * Klick fängt die Arbeitsfläche ab, damit die Seite nicht neu lädt — „zurück"
 * führt dann wieder in die Maske, aus der man kam.
 */
export function Maskenverweis({
  maske,
  beiKlick,
  title,
  className,
  children,
}: {
  readonly maske: Maske;
  readonly beiKlick: () => void;
  readonly title?: string;
  readonly className?: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <a
      href={pfadVon(maske)}
      {...(title === undefined ? {} : { title })}
      {...(className === undefined ? {} : { className })}
      onClick={(e) => {
        /* Neuer Tab, neues Fenster: Das tut der Browser selbst. */
        if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        beiKlick();
      }}
    >
      {children}
    </a>
  );
}

/**
 * DAS DATUM ALS SPRUNG IN DEN TAGESPLAN (TAG-29, Auftrag CRM-67).
 *
 * Aus den Terminen (CRM-67) und aus den Anfragen (CRM-69) derselbe Verweis
 * auf denselben Einstieg `/trainingsplanung/<tag>/<standort>`.
 *
 * Ohne `beiWahl` (kein `tagesplan/sehen`, BEN-02) bleibt das Datum Text:
 * ein Verweis, der in eine verbotene Maske führt, wäre eine Sackgasse.
 */
export function Tagesverweis({
  tag,
  standort,
  beiWahl,
  className,
  children,
}: {
  readonly tag: string;
  readonly standort: string | undefined;
  readonly beiWahl: ((tag: string, standort: string | undefined) => void) | undefined;
  readonly className?: string;
  readonly children: ReactNode;
}): JSX.Element {
  if (beiWahl === undefined) return <>{children}</>;
  return (
    <Maskenverweis
      maske={{ art: 'trainingsplanung', tag, ...(standort === undefined ? {} : { standort }) }}
      title="In der Trainingsplanung öffnen"
      {...(className === undefined ? {} : { className })}
      beiKlick={() => {
        beiWahl(tag, standort);
      }}
    >
      {children}
      <span className="nur-vorlesen"> — in der Trainingsplanung öffnen</span>
    </Maskenverweis>
  );
}
